import React, { createContext, useContext, useEffect, useState } from 'react';
import apiService from '../services/ApiService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : null;
};

const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

const parseStoredUser = () => {
  const rawUser = getCookie('user_data');
  if (!rawUser) return null;

  try {
    return JSON.parse(decodeURIComponent(rawUser));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const authToken = getCookie('auth_token');
    const storedUser = parseStoredUser();

    if (authToken && storedUser) {
      apiService.setAuthToken(authToken);
      setUser(storedUser);
      setIsAuthenticated(true);
    }

    setIsLoading(false);
  }, []);

  const login = async (userData) => {
    const roleId = userData.idRol || userData.roleId || null;
    const roleName = userData.nombreRol || userData.roleName || userData.role || 'Operador';
    const userToStore = {
      id: userData.id,
      email: userData.email,
      name: userData.name || userData.displayName || userData.email,
      displayName: userData.displayName || userData.name || userData.email,
      token: userData.token,
      roleId,
      roleName,
      role: roleName,
      idTenant: userData.idTenant,
      tenantName: userData.tenantName,
      tenantSchemaName: userData.tenantSchemaName,
      pictureUrl: userData.pictureUrl || '',
      lastLogin: new Date().toISOString()
    };

    setCookie('auth_token', userData.token, 7);
    setCookie('user_data', encodeURIComponent(JSON.stringify(userToStore)), 7);
    apiService.setAuthToken(userData.token);
    setUser(userToStore);
    setIsAuthenticated(true);

    return { success: true, user: userToStore };
  };

  const logout = () => {
    deleteCookie('auth_token');
    deleteCookie('user_data');
    apiService.setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (newUserData) => {
    const updatedUser = { ...user, ...newUserData };
    setUser(updatedUser);
    setCookie('user_data', encodeURIComponent(JSON.stringify(updatedUser)), 7);
  };

  const contextValue = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
